import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getGroups, createGroup } from '../api/client';
import LoadingLeaf from '../components/LoadingLeaf';

export default function Groups() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(searchParams.get('new') === '1');
    const [form, setForm] = useState({ name: '', description: '' });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => { fetchGroups(); }, []);
    useEffect(() => {
        if (searchParams.get('new') === '1') setSearchParams({}, { replace: true });
    }, []);

    const fetchGroups = async () => {
        try {
            const res = await getGroups();
            setGroups(res.data || []);
        } catch (err) {
            setError('Could not load groups.');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) return;
        setSaving(true);
        try {
            const res = await createGroup(form);
            setGroups(prev => [...prev, res.data]);
            setForm({ name: '', description: '' });
            setShowModal(false);
        } catch (err) {
            setError('Could not create group.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={styles.page}>

            {/* Header */}
            <div style={styles.header}>
                <div>
                    <div style={styles.pageTitle}>Groups</div>
                    <div style={styles.pageSubtitle}>{groups.length} group{groups.length !== 1 ? 's' : ''}</div>
                </div>
                <button style={styles.addBtn} onClick={() => setShowModal(true)}>
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="2" x2="8" y2="14" /><line x1="2" y1="8" x2="14" y2="8" /></svg>
                    New group
                </button>
            </div>

            {error && <div style={styles.errorAlert}>{error}</div>}

            {loading ? (
                <div style={styles.loadingState}><LoadingLeaf size={48} label="Loading groups…" /></div>
            ) : groups.length === 0 ? (
                <div style={styles.emptyState}>
                    <div style={styles.emptyIcon}>◎</div>
                    <h3 style={styles.emptyTitle}>No groups yet</h3>
                    <p style={styles.emptyText}>Create a group to tag clients and build shared modalities.</p>
                    <button style={styles.emptyBtn} onClick={() => setShowModal(true)}>Create first group</button>
                </div>
            ) : (
                <div style={styles.groupGrid}>
                    {groups.map(group => (
                        <div
                            key={group.id}
                            style={styles.groupCard}
                            className="tc-card-hover"
                            onClick={() => navigate(`/groups/${group.id}`)}
                        >
                            <div style={styles.groupCardTop}>
                                <div style={styles.groupIcon}>◎</div>
                            </div>
                            <div style={styles.groupName}>{group.name}</div>
                            {group.description && <div style={styles.groupDesc}>{group.description}</div>}
                            <div style={styles.groupMeta}>
                                <span style={styles.groupMetaPill}>{group.client_count || 0} client{group.client_count !== 1 ? 's' : ''}</span>
                                {group.modality_updated_at && <span style={styles.groupMetaPill}>Modality built</span>}
                            </div>
                            <div style={styles.groupCardFooter}>
                                <span style={styles.viewGroup}>View group →</span>
                            </div>
                        </div>
                    ))}

                    {/* Add group card */}
                    <div style={styles.addCard} onClick={() => setShowModal(true)}>
                        <div style={styles.addCardIcon}>+</div>
                        <div style={styles.addCardLabel}>New group</div>
                    </div>
                </div>
            )}

            {/* New Group Modal */}
            {showModal && (
                <div style={styles.modalOverlay} onClick={() => setShowModal(false)}>
                    <div style={styles.modal} onClick={e => e.stopPropagation()}>
                        <div style={styles.modalHeader}>
                            <div style={styles.modalTitle}>Create a new group</div>
                            <button style={styles.modalClose} onClick={() => setShowModal(false)}>×</button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>Group name</label>
                                <input
                                    style={styles.input}
                                    value={form.name}
                                    onChange={e => setForm({ ...form, name: e.target.value })}
                                    placeholder="e.g. Tuesday Evening Cohort"
                                    autoFocus
                                    required
                                />
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>Description (optional)</label>
                                <textarea
                                    style={styles.textarea}
                                    value={form.description}
                                    onChange={e => setForm({ ...form, description: e.target.value })}
                                    placeholder="What brings this group together..."
                                />
                            </div>
                            <div style={styles.modalFooter}>
                                <button type="button" style={styles.cancelBtn} onClick={() => setShowModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" style={styles.saveBtn} disabled={saving}>
                                    {saving ? 'Creating...' : 'Create group'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

const styles = {
    page: { padding: '36px 40px', fontFamily: "'DM Sans', sans-serif" },
    header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '28px' },
    pageTitle: { fontFamily: "'Lora', Georgia, serif", fontSize: '28px', fontWeight: '400', color: 'var(--text-dark)', marginBottom: '4px' },
    pageSubtitle: { fontSize: '13px', color: 'var(--text-soft)' },
    addBtn: { display: 'flex', alignItems: 'center', gap: '8px', padding: '11px 20px', borderRadius: '10px', background: 'var(--sage)', border: 'none', color: 'white', fontFamily: "'DM Sans', sans-serif", fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
    errorAlert: { background: 'var(--red-light)', color: 'var(--red)', padding: '12px 16px', borderRadius: '8px', fontSize: '13.5px', marginBottom: '16px' },
    loadingState: { padding: '60px', textAlign: 'center', color: 'var(--text-soft)', fontSize: '14px' },
    emptyState: { textAlign: 'center', padding: '80px 24px', color: 'var(--text-soft)' },
    emptyIcon: { fontSize: '40px', marginBottom: '16px', color: 'var(--text-faint)' },
    emptyTitle: { fontFamily: "'Lora', Georgia, serif", fontSize: '22px', fontWeight: '400', color: 'var(--text-dark)', marginBottom: '8px' },
    emptyText: { fontSize: '14px', marginBottom: '20px' },
    emptyBtn: { padding: '11px 22px', borderRadius: '10px', background: 'var(--sage)', border: 'none', color: 'white', fontFamily: "'DM Sans', sans-serif", fontSize: '14px', cursor: 'pointer' },
    groupGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '16px' },
    groupCard: { background: 'white', borderRadius: '14px', border: '1px solid var(--border)', padding: '20px', cursor: 'pointer', boxShadow: 'var(--shadow-sm)', display: 'flex', flexDirection: 'column' },
    groupCardTop: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '14px' },
    groupIcon: { width: '44px', height: '44px', borderRadius: '50%', background: 'var(--mint)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '17px', color: 'var(--sage)' },
    groupName: { fontFamily: "'Lora', Georgia, serif", fontSize: '17px', fontWeight: '400', color: 'var(--text-dark)', marginBottom: '4px' },
    groupDesc: { fontSize: '12.5px', color: 'var(--text-soft)', marginBottom: '10px', lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' },
    groupMeta: { display: 'flex', gap: '6px', flexWrap: 'wrap' },
    groupMetaPill: { background: 'var(--mint)', color: 'var(--sage)', borderRadius: '20px', fontSize: '11px', fontWeight: '500', padding: '3px 10px' },
    groupCardFooter: { marginTop: 'auto', paddingTop: '16px' },
    viewGroup: { fontSize: '12.5px', color: 'var(--sage)', fontWeight: '500' },
    addCard: { background: 'none', borderRadius: '14px', border: '2px dashed var(--border)', padding: '20px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '160px', transition: 'border-color 0.15s, background 0.15s', gap: '8px' },
    addCardIcon: { width: '40px', height: '40px', borderRadius: '50%', background: 'var(--mint)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', color: 'var(--sage)', fontWeight: '300' },
    addCardLabel: { fontSize: '13.5px', color: 'var(--text-soft)', fontWeight: '500' },
    modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(26,46,40,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, backdropFilter: 'blur(2px)' },
    modal: { background: 'white', borderRadius: '16px', width: '480px', maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: 'var(--shadow-lg)' },
    modalHeader: { padding: '24px 28px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' },
    modalTitle: { fontFamily: "'Lora', Georgia, serif", fontSize: '22px', fontWeight: '400', color: 'var(--text-dark)' },
    modalClose: { background: 'none', border: 'none', fontSize: '24px', color: 'var(--text-faint)', cursor: 'pointer', lineHeight: 1 },
    formGroup: { marginBottom: '18px', padding: '0 28px' },
    label: { display: 'block', fontSize: '11.5px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-soft)', marginBottom: '8px' },
    input: { width: '100%', padding: '11px 14px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--cream)', fontFamily: "'DM Sans', sans-serif", fontSize: '14px', color: 'var(--text-dark)', outline: 'none', boxSizing: 'border-box' },
    textarea: { width: '100%', padding: '11px 14px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--cream)', fontFamily: "'DM Sans', sans-serif", fontSize: '14px', color: 'var(--text-dark)', outline: 'none', boxSizing: 'border-box', minHeight: '90px', resize: 'vertical' },
    modalFooter: { display: 'flex', gap: '10px', justifyContent: 'flex-end', padding: '16px 28px 24px', borderTop: '1px solid var(--border-light)' },
    cancelBtn: { padding: '10px 18px', borderRadius: '8px', background: 'none', border: '1px solid var(--border)', color: 'var(--text-mid)', fontFamily: "'DM Sans', sans-serif", fontSize: '13.5px', cursor: 'pointer' },
    saveBtn: { padding: '10px 20px', borderRadius: '8px', background: 'var(--sage)', border: 'none', color: 'white', fontFamily: "'DM Sans', sans-serif", fontSize: '13.5px', cursor: 'pointer', fontWeight: '500' },
};
